import { useQuery } from "@tanstack/react-query";
import { fetcher } from "@/shared/api";
import { planKeys } from "./queryKeys";
import type { Plan } from "../model/types";
import type { ListQueryParams } from "@/shared/types/list";
import { toQueryString } from "@/shared/utils/toQueryString";

/** `/system/plan` offers no status filter — search covers name and code.
 *
 *  `enabled` is a CLIENT concern, not a query parameter: it is stripped before
 *  the URL and the query key are built, so gating a call cannot accidentally
 *  produce a second cache entry for the same request. Mirrors
 *  `useSystemLogisticsList`. */
export type SystemPlanListParams = ListQueryParams & { enabled?: boolean };

export function useSystemPlanList(params: SystemPlanListParams = {}) {
  const { enabled = true, ...query } = params;
  return useQuery({
    queryKey: planKeys.systemList(query as Record<string, unknown>),
    queryFn: () => fetcher(`/plan${toQueryString(query)}`),
    enabled,
    keepPreviousData: true,
  });
}

/** Mirrors the backend's `MerchantGracePeriodStatus` (subscription-access.ts). Only ever
 *  `active: true` while `status` is EXPIRED and within 14 days of the lapse — see
 *  `isMerchantDashboardAccessAllowed`, which is what actually keeps the dashboard reachable
 *  while this is true. */
export interface GracePeriodInfo {
  active: boolean;
  endsAt: string | null;
  daysRemaining: number | null;
}

interface CurrentPlanResponse {
  data: {
    plan: { code: string };
    /** "MONTHLY" | "YEARLY" — backend reports this directly, prefer over
     *  any client-side heuristic based on period length. */
    billingInterval?: "MONTHLY" | "YEARLY";
    currentPeriodStart?: string;
    currentPeriodEnd: string;
    status?: "TRIALING" | "ACTIVE" | "EXPIRED" | "CANCELED";
    /** Provider used on the last successful charge — drives the
     *  "skip Stripe redirect" branch for returning Stripe customers
     *  (BE charges the saved card directly when this is "STRIPE"). */
    paymentProvider?: "STRIPE" | "FONEPAY" | string;
    gracePeriod?: GracePeriodInfo;
    [key: string]: unknown;
  };
}

interface BillingPlansResponse {
  data: unknown[];
}

interface OnboardingPlansResponse {
  success?: boolean;
  message?: string;
  data: Plan[];
}

/**
 * Fetches the list of plans available during onboarding (free trial + paid tiers).
 * Lighter than `useBillingData` — does not also fetch the subscriber's current plan.
 */
export function useOnboardingPlans() {
  return useQuery({
    queryKey: planKeys.plans(),
    queryFn: () => fetcher<OnboardingPlansResponse>("/onboarding-v2/plans"),
  });
}

export function useBillingData() {
  const currentPlanQuery = useQuery({
    queryKey: planKeys.currentPlan(),
    queryFn: () => fetcher<CurrentPlanResponse>("/subscriptions/my"),
  });

  const billingPlansQuery = useQuery({
    queryKey: planKeys.plans(),
    queryFn: () => fetcher<BillingPlansResponse>("/onboarding-v2/plans"),
  });

  return {
    currentPlan: currentPlanQuery.data,
    billingPlans: billingPlansQuery.data,
    isLoading: currentPlanQuery.isLoading || billingPlansQuery.isLoading,
    isError: currentPlanQuery.isError || billingPlansQuery.isError,
    error: currentPlanQuery.error || billingPlansQuery.error,
  };
}

